import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { api } from '../services/api'
import toast from 'react-hot-toast'

const AppContext = createContext()

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  currentScan: null,
  scanHistory: [],
  error: null
}

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  SET_CURRENT_SCAN: 'SET_CURRENT_SCAN',
  SET_SCAN_HISTORY: 'SET_SCAN_HISTORY',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  LOGOUT: 'LOGOUT'
}

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload }
    
    case ActionTypes.SET_USER:
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload
      }
    
    case ActionTypes.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload }
    
    case ActionTypes.SET_CURRENT_SCAN:
      return { ...state, currentScan: action.payload }
    
    case ActionTypes.SET_SCAN_HISTORY:
      return { ...state, scanHistory: action.payload }
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload }
    
    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null }
    
    case ActionTypes.LOGOUT:
      return {
        ...initialState,
        isAuthenticated: false
      }
    
    default:
      return state
  }
}

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // Check for existing authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (token) {
      // Verify token and get user data
      verifyAuth()
    }
  }, [])

  // Actions
  const setLoading = (loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading })
  }

  const setUser = (user) => {
    dispatch({ type: ActionTypes.SET_USER, payload: user })
  }

  const setError = (error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error })
    toast.error(error)
  }

  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR })
  }

  const logout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    dispatch({ type: ActionTypes.LOGOUT })
    toast.success('Logged out successfully')
  }

  // API functions
  const sendOTP = async (phoneNumber) => {
    try {
      setLoading(true)
      const response = await api.post('/auth/send-otp', { phone_number: phoneNumber })
      toast.success('OTP sent successfully!')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send OTP')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const verifyOTP = async (phoneNumber, otpCode) => {
    try {
      setLoading(true)
      const response = await api.post('/auth/verify-otp', {
        phone_number: phoneNumber,
        otp_code: otpCode
      })
      
      const { access_token, refresh_token } = response.data
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      
      // Decode JWT token to get user ID
      const tokenPayload = JSON.parse(atob(access_token.split('.')[1]))
      const userId = tokenPayload.sub
      
      // Get user profile using the decoded user ID
      await getUserProfile(userId)
      
      toast.success('Authentication successful!')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to verify OTP')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getUserProfile = async (userId = null) => {
    try {
      const targetUserId = userId || state.user?.id
      if (!targetUserId) {
        throw new Error('User ID is required')
      }
      const response = await api.get(`/user/profile/${targetUserId}`)
      setUser(response.data)
      return response.data
    } catch (error) {
      console.error('Failed to get user profile:', error)
      throw error
    }
  }

  const updateUserProfile = async (userData) => {
    try {
      setLoading(true)
      const response = await api.post('/user/profile', userData)
      setUser(response.data)
      toast.success('Profile updated successfully!')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile'
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to update profile')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const verifyAuth = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) return false

      // Set token in API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      // Try to get user profile to verify token
      const response = await api.get('/user/profile')
      setUser(response.data)
      return true
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      return false
    }
  }

  const startScan = async (userData) => {
    try {
      setLoading(true)
      const response = await api.post('/scan/start', {
        user_id: state.user.id
      })
      
      dispatch({ type: ActionTypes.SET_CURRENT_SCAN, payload: response.data })
      toast.success('Scan started successfully!')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to start scan')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getScanProgress = async (scanId) => {
    try {
      const response = await api.get(`/scan/progress/${scanId}`)
      return response.data
    } catch (error) {
      console.error('Failed to get scan progress:', error)
      throw error
    }
  }

  const getScanResult = async (scanId) => {
    try {
      const response = await api.get(`/scan/result/${scanId}`)
      dispatch({ type: ActionTypes.SET_CURRENT_SCAN, payload: response.data })
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get scan result')
      throw error
    }
  }

  const getScanHistory = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/user/${state.user.id}/scans`)
      dispatch({ type: ActionTypes.SET_SCAN_HISTORY, payload: response.data })
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get scan history')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getReport = async (scanId) => {
    try {
      const response = await api.get(`/report/${scanId}`)
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get report')
      throw error
    }
  }

  const downloadPDF = async (scanId) => {
    try {
      const response = await api.get(`/report/${scanId}/pdf`, {
        responseType: 'blob'
      })
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Create shorter filename with user name and short report ID
      const userName = state.user?.name || 'User'
      const shortId = scanId.slice(-8) // Last 8 characters of UUID
      const fileName = `${userName.replace(/\s+/g, '_')}_Health_Report_${shortId}.pdf`
      
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('PDF report downloaded successfully!')
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to download PDF')
      throw error
    }
  }

  const detectFace = async (imageFile) => {
    try {
      const formData = new FormData()
      formData.append('file', imageFile)
      
      const response = await api.post('/scan/detect-face', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Face detection failed')
      throw error
    }
  }

  const checkImageQuality = async (imageFile) => {
    try {
      const formData = new FormData()
      formData.append('file', imageFile)
      
      const response = await api.post('/scan/quality-check', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Image quality check failed')
      throw error
    }
  }

  // HRA API functions
  const getHRAStatus = async () => {
    try {
      const response = await api.get('/hra/status')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get HRA status')
      throw error
    }
  }

  const getHRAQuestions = async () => {
    try {
      const response = await api.get('/hra/questionnaire/questions')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load HRA questions')
      throw error
    }
  }

  const submitHRAQuestionnaire = async (questionnaireData) => {
    try {
      setLoading(true)
      const response = await api.post('/hra/questionnaire', questionnaireData)
      toast.success('Health Risk Assessment completed successfully!')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit HRA questionnaire')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getCurrentRiskAssessment = async () => {
    try {
      const response = await api.get('/hra/risk-assessment')
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get risk assessment')
      throw error
    }
  }

  const getHRAHistory = async (limit = 10) => {
    try {
      const response = await api.get(`/hra/history?limit=${limit}`)
      return response.data
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to get HRA history')
      throw error
    }
  }

  // Generic API call function for HRA components
  const apiCall = async (endpoint, method = 'GET', data = null) => {
    try {
      const config = {
        method: method.toLowerCase(),
        url: endpoint
      }
      
      if (data) {
        config.data = data
      }
      
      const response = await api(config)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || 'API call failed'
      setError(errorMessage)
      throw error
    }
  }

  const value = {
    // State
    ...state,
    
    // Actions
    setLoading,
    setUser,
    setError,
    clearError,
    logout,
    
    // API functions
    sendOTP,
    verifyOTP,
    getUserProfile,
    updateUserProfile,
    verifyAuth,
    startScan,
    getScanProgress,
    getScanResult,
    getScanHistory,
    getReport,
    downloadPDF,
    detectFace,
    checkImageQuality,
    
    // HRA API functions
    getHRAStatus,
    getHRAQuestions,
    submitHRAQuestionnaire,
    getCurrentRiskAssessment,
    getHRAHistory,
    apiCall
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

// Custom hook to use the context
export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
